// src/components/SupplierHoursButton.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useIsMobile from "../hooks/isMobile";
import "./SupplierHoursButton.css";

const GIORNI_ORDINE = [
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];

const GIORNI_ABBREV = {
  Lunedì: "Lun",
  Martedì: "Mar",
  Mercoledì: "Mer",
  Giovedì: "Gio",
  Venerdì: "Ven",
  Sabato: "Sab",
  Domenica: "Dom",
};

function todayGiornoSettimana() {
  // getDay(): 0=domenica..6=sabato -> riallineo a GIORNI_ORDINE (0=lunedì)
  const idx = (new Date().getDay() + 6) % 7;
  return GIORNI_ORDINE[idx];
}

// "09:00:00" | "09:00" -> "09:00" (via i secondi, formato Postgres TIME).
function normalizeTime(value) {
  const s = String(value || "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : s;
}

// Firma a fedeltà piena (orari + tipi) usata SOLO per capire se due giorni
// hanno esattamente le stesse fasce e possono quindi essere raggruppati
// (es. "Lun–Ven"). Un fornitore con fasce multiple diverse giorno per
// giorno semplicemente non si raggrupperà: resta un blocco per giorno.
function daySignature(slots) {
  return slots
    .map((s) => `${s.orario_inizio}-${s.orario_fine}-${s.tipo || ""}`)
    .join("|");
}

/**
 * Raggruppa le fasce orarie grezze in blocchi per giorno ordinati Lun→Dom.
 * Un giorno può avere PIÙ fasce (ciascuna con proprio orario+tipo): tutte
 * vengono preservate e restituite ordinate per orario di inizio. Giorni
 * consecutivi con l'identico set di fasce vengono uniti in un'unica
 * etichetta (es. "Lun–Ven"); giorni senza fasce diventano "Chiuso" (anche
 * questi raggruppabili tra loro).
 */
export function formatSupplierHours(orari) {
  const list = Array.isArray(orari) ? orari : [];

  const byGiorno = new Map();
  for (const g of GIORNI_ORDINE) byGiorno.set(g, []);
  for (const f of list) {
    if (!byGiorno.has(f?.giorno_settimana)) continue;
    byGiorno.get(f.giorno_settimana).push(f);
  }

  const today = todayGiornoSettimana();

  const perDay = GIORNI_ORDINE.map((giorno) => {
    const slots = (byGiorno.get(giorno) || [])
      .map((s) => ({
        orario_inizio: normalizeTime(s.orario_inizio),
        orario_fine: normalizeTime(s.orario_fine),
        tipo: (s.tipo || "").toLowerCase(),
      }))
      .sort((a, b) => a.orario_inizio.localeCompare(b.orario_inizio));

    return {
      giorno,
      slots,
      signature: daySignature(slots),
      isToday: giorno === today,
    };
  });

  const rows = [];
  let i = 0;
  while (i < perDay.length) {
    let j = i;
    while (j + 1 < perDay.length && perDay[j + 1].signature === perDay[i].signature) j++;

    const start = perDay[i];
    const end = perDay[j];
    const spanIncludesToday = perDay
      .slice(i, j + 1)
      .some((d) => d.isToday);

    rows.push({
      label:
        j === i
          ? start.giorno
          : `${GIORNI_ABBREV[start.giorno]}–${GIORNI_ABBREV[end.giorno]}`,
      slots: start.slots,
      isToday: spanIncludesToday,
    });

    i = j + 1;
  }

  return rows;
}

const TIPO_LABELS = {
  ritiro: "Ritiro",
  consegna: "Consegna",
  entrambi: "Entrambi",
};

function TipoPill({ tipo }) {
  const key = (tipo || "").toLowerCase();
  const cls =
    key === "ritiro" ? "is-ritiro" : key === "consegna" ? "is-consegna" : "is-entrambi";
  return (
    <span className={`shb-tipo-pill ${cls}`}>{TIPO_LABELS[key] || tipo || "—"}</span>
  );
}

/**
 * Pulsante "Orari" + popover condiviso da scheda fornitore e risultati
 * ricerca. Apertura SOLO su click/tap (mai hover). Il contenuto è renderizzato
 * in un portal su document.body: non è mai tagliato da un overflow:hidden
 * antenato e non risente delle transizioni hover/transform della card che lo
 * ospita (causa dello sfarfallamento nella versione precedente).
 *
 * Stato "aperto" controllato dal chiamante (isOpen/onToggle/onClose): è così
 * che si garantisce un solo popup aperto alla volta quando ci sono più
 * fornitori in lista (il chiamante tiene un solo id "aperto").
 */
export default function SupplierHoursButton({
  orari,
  isOpen,
  onToggle,
  onClose,
  label,
}) {
  const isMobile = useIsMobile();
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const rows = useMemo(() => formatSupplierHours(orari), [orari]);

  // Posizione ancorata al pulsante, ricalcolata solo mentre il popover è
  // aperto (mai nel render, mai in modo incondizionato). Clampata ai bordi
  // del viewport: non esce mai a destra/sotto, anche su desktop stretto.
  useLayoutEffect(() => {
    if (!isOpen || isMobile) return;

    const MARGIN = 8;

    const updatePosition = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();

      // Il popover è già nel DOM (portal) anche prima del primo calcolo:
      // la sua larghezza/altezza (da CSS) sono già misurabili qui, la sua
      // posizione ancora no — per questo si clampa in un solo passaggio.
      const popWidth = popoverRef.current?.offsetWidth || 260;
      const popHeight = popoverRef.current?.offsetHeight || 0;

      let left = rect.left;
      left = Math.min(left, window.innerWidth - popWidth - MARGIN);
      left = Math.max(MARGIN, left);

      let top = rect.bottom + MARGIN;
      if (popHeight && top + popHeight > window.innerHeight - MARGIN) {
        // Non c'è spazio sotto: apre sopra al pulsante.
        top = Math.max(MARGIN, rect.top - popHeight - MARGIN);
      }

      setCoords({ top, left });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, isMobile]);

  // Click fuori / Escape chiudono (desktop e mobile).
  useEffect(() => {
    if (!isOpen) return;

    const onDocPointerDown = (e) => {
      const inButton = buttonRef.current?.contains(e.target);
      const inPopover = popoverRef.current?.contains(e.target);
      if (!inButton && !inPopover) onClose?.();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const content = (
    <div className="shb-rows">
      {rows.length ? (
        rows.map((r, idx) => (
          <div
            key={idx}
            className={`shb-day-group ${r.isToday ? "is-today" : ""}`}
          >
            <div className="shb-day-label">{r.label}</div>

            {r.slots.length ? (
              <div className="shb-slots">
                {r.slots.map((s, sIdx) => (
                  <div key={sIdx} className="shb-slot">
                    <span className="shb-time">
                      {s.orario_inizio}–{s.orario_fine}
                    </span>
                    <TipoPill tipo={s.tipo} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="shb-slot shb-slot--closed">Chiuso</div>
            )}
          </div>
        ))
      ) : (
        <div className="shb-empty">Nessuna fascia oraria</div>
      )}
    </div>
  );

  return (
    <div className="shb-wrap">
      <button
        ref={buttonRef}
        type="button"
        className="pill pressable btn-hours"
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.();
        }}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        title="Fasce orarie"
      >
        🕒 Orari
      </button>

      {isOpen &&
        createPortal(
          isMobile ? (
            <div
              className="shb-sheet-backdrop"
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
            >
              <div
                className="shb-sheet"
                ref={popoverRef}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="shb-sheet-header">
                  <strong>{label ? `Fasce – ${label}` : "Fasce orarie"}</strong>
                  <button
                    className="shb-close"
                    onClick={() => onClose?.()}
                    aria-label="Chiudi"
                  >
                    ✕
                  </button>
                </div>
                {content}
              </div>
            </div>
          ) : (
            <div
              ref={popoverRef}
              className="shb-popover"
              role="dialog"
              aria-label={label ? `Orari di ${label}` : "Orari"}
              style={
                coords
                  ? { top: coords.top, left: coords.left }
                  : { visibility: "hidden" }
              }
              onClick={(e) => e.stopPropagation()}
            >
              {content}
            </div>
          ),
          document.body,
        )}
    </div>
  );
}
